import { useEffect, useRef } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { translateText } from "../../utils/autoTranslate";

const ATTRIBUTES_TO_TRANSLATE = ["placeholder", "title", "aria-label"];

export default function AutoTranslate() {
  const { language } = useLanguage();
  const originalTextMap = useRef(new WeakMap<Text, string>());
  const originalAttrMap = useRef(
    new WeakMap<Element, Partial<Record<(typeof ATTRIBUTES_TO_TRANSLATE)[number], string>>>()
  );
  const lastAppliedTextMap = useRef(new WeakMap<Text, string>());
  const isApplying = useRef(false);
  const scheduledApply = useRef<number | null>(null);

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;

    const applyTranslations = () => {
      if (isApplying.current) return;
      isApplying.current = true;

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let current = walker.nextNode();

      while (current) {
        const textNode = current as Text;
        const parent = textNode.parentElement;
        if (
          parent &&
          !["SCRIPT", "STYLE", "NOSCRIPT"].includes(parent.tagName) &&
          !parent.closest("svg")
        ) {
          const original =
            originalTextMap.current.get(textNode) ?? textNode.nodeValue ?? "";
          if (!originalTextMap.current.has(textNode)) {
            originalTextMap.current.set(textNode, original);
          }

          const trimmed = original.trim();
          if (trimmed) {
            const translated = translateText(trimmed, language);
            const leading = original.match(/^\s*/)?.[0] ?? "";
            const trailing = original.match(/\s*$/)?.[0] ?? "";
            const nextValue = `${leading}${translated}${trailing}`;
            if (textNode.nodeValue !== nextValue) {
              textNode.nodeValue = nextValue;
              lastAppliedTextMap.current.set(textNode, nextValue);
            }
          }
        }
        current = walker.nextNode();
      }

      const allElements = root.querySelectorAll("*");
      allElements.forEach((element) => {
        const stored = originalAttrMap.current.get(element) ?? {};
        ATTRIBUTES_TO_TRANSLATE.forEach((attr) => {
          const value = element.getAttribute(attr);
          if (!value) return;
          if (!(attr in stored)) {
            stored[attr] = value;
          }
          const originalValue = stored[attr] ?? value;
          const translatedValue = translateText(originalValue, language);
          if (value !== translatedValue) {
            element.setAttribute(attr, translatedValue);
          }
        });
        originalAttrMap.current.set(element, stored);
      });

      isApplying.current = false;
    };

    applyTranslations();

    const scheduleApplyTranslations = () => {
      if (scheduledApply.current !== null) return;
      scheduledApply.current = window.setTimeout(() => {
        scheduledApply.current = null;
        applyTranslations();
      }, 0);
    };

    const observer = new MutationObserver((mutations) => {
      if (isApplying.current) return;

      // Keep source text in sync with React updates so dynamic labels
      // (like selected language) are not overwritten by stale cached text.
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData" && mutation.target.nodeType === Node.TEXT_NODE) {
          const textNode = mutation.target as Text;
          const nextValue = textNode.nodeValue ?? "";
          const lastApplied = lastAppliedTextMap.current.get(textNode);
          if (lastApplied !== undefined && lastApplied === nextValue) {
            lastAppliedTextMap.current.delete(textNode);
            return;
          }
          originalTextMap.current.set(textNode, nextValue);
        }
      });

      scheduleApplyTranslations();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      if (scheduledApply.current !== null) {
        window.clearTimeout(scheduledApply.current);
      }
    };
  }, [language]);

  return null;
}
